import {
	SET_PRINCIPAL,
	SET_ACCESS_TOKEN,
	SET_REQUEST_VERIFICATION_TOKEN,
	SET_DO_LOGOUT
} from './principal-mutation-types';
import userApi from '@/services/user-api-service';
import cookies from 'vue-cookies';
import store from '@/store/index.js';
import objectIsEmpty from '@/utils/object-is-empty';
import dispatchWindowEvent from '@/utils/dispatch-window-event';
import { FEATURE_SWITCHES } from '@/constants/feature-switches';
import posthog from 'posthog-js';
import { isEmpty } from 'lodash';

const posthogHeatmaps = process.env.VUE_APP_POSTHOG_HEATMAPS === 'true';
/**
 * A wrapper for a vue user (if come from AccessWeb) or oidc user (if coming from Vue.Authentication login.)
 */
export default {
	strict: process.env.NODE_ENV !== 'production',
	name: 'principal',
	namespaced: true,
	state: {
		principal: {},
		accessToken: cookies.get('vue2tkn'),
		requestVerificationToken: cookies.get('__RequestVerificationToken'),
		userInfoPath: '/connect/userinfo',
		userLoadedEventName: 'principal:userLoaded',
		useOidc: process.env.VUE_APP_USE_OIDC,
		doLogout: false
	},
	mutations: {
		[SET_PRINCIPAL](state, principal) {
			//user first and last name claims on the principal may be arrays
			//when both an employee and global employee
			if (principal.FirstName && Array.isArray(principal.FirstName)) {
				principal.FirstName = principal.FirstName[0];
			}

			if (principal.LastName && Array.isArray(principal.LastName)) {
				principal.LastName = principal.LastName[0];
			}

			state.principal = principal;
		},
		[SET_ACCESS_TOKEN](state, accessToken) {
			state.accessToken = accessToken;
		},
		[SET_REQUEST_VERIFICATION_TOKEN](state, token) {
			state.requestVerificationToken = token;
		},
		[SET_DO_LOGOUT](state, doLogout) {
			state.doLogout = doLogout;
		}
	},
	actions: {
		// actions let us get to ({ state, getters, commit, dispatch }) {
		async getUserInfoAction({ commit, state }) {
			const principal = await userApi.get(state.userInfoPath);

			commit(SET_PRINCIPAL, principal);
		},
		setUserInfoAction({ commit }, userInfo) {
			commit(SET_PRINCIPAL, userInfo);
			if (userInfo?.EnabledFeatureSwitches?.includes(`${FEATURE_SWITCHES.POSTHOG}`) && !isEmpty(process.env.VUE_APP_POSTHOG_KEY)) {
				posthog.init(process.env.VUE_APP_POSTHOG_KEY, { 
					api_host: 'https://us.i.posthog.com',
					enable_heatmaps: posthogHeatmaps,
				});
				posthog.identify(`${userInfo.Email}_${userInfo.AspNetCompaniesId}`,{
					$email: userInfo.Email,
					$first_name: userInfo.FirstName,
					$last_name: userInfo.LastName,
					$employee_id: userInfo.EmployeeId,
					AspNetCompaniesId: userInfo.AspNetCompaniesId,
					DealershipGroupNumber: userInfo.DealershipGroupNumber,
					CompanyName: userInfo.CurrentCompanyName,
					EmployeeId: userInfo.EmployeeId,
				});
			}
		},
		dispatchUserLoadedEvent({ state }) {
			const user = state.user;

			dispatchWindowEvent(state.userLoadedEventName, user);
		},
		signOut() {},
		removeUser({ commit }) {
			commit(SET_PRINCIPAL, {});
			store.dispatch('oidcStore/removeOidcUser');
		},
		silentRenew() {
			store.dispatch('oidcStore/authenticateOidcSilent').then(
				user => {
					console.log(user);
				},
				error => {
					console.log(error);
				}
			);
		},
		async checkAccess(context, route) {
			return new Promise(resolve => {
				if (
					context.getters.accessToken &&
					context.getters.accessToken !== 'undefined'
				) {
					if (objectIsEmpty(context.getters.user)) {
						context.dispatch('getUserInfoAction').then(
							() => {
								if (!objectIsEmpty(context.getters.user)) {
									context.dispatch('dispatchUserLoadedEvent');

									const oidcUser = {
										...context.getters.user
									};

									oidcUser.access_token =
										context.getters.accessToken;

									store.dispatch(
										'oidcStore/oidcWasAuthenticated',
										oidcUser
									);

									resolve(true);
								} else {
									store
										.dispatch(
											'oidcStore/authenticateOidcSilent'
										)
										.then(
											user => {
												console.log(user);
												resolve(true);
											},
											error => {
												console.log(error);
											}
										);

									resolve(false);
								}
							},
							error => {
								console.log(error);

								store
									.dispatch(
										'oidcStore/authenticateOidcSilent'
									)
									.then(
										user => {
											console.log(user);
											resolve(true);
										},
										error => {
											console.log(error);
											resolve(false);
										}
									);

								resolve(false);
							}
						);
					} else {
						resolve(true);
					}
				} else if (!context.getters.useOidc) {
					context.commit(SET_DO_LOGOUT, true);
					resolve(true);
				} else {
					store
						.dispatch('oidcStore/oidcCheckAccess', route)
						.then(hasAccess => {
							if (hasAccess) {
								context.commit(
									SET_PRINCIPAL,
									store.getters['oidcStore/oidcUser']
								);

								context.commit(
									SET_ACCESS_TOKEN,
									store.getters['oidcStore/oidcAccessToken']
								);

								context.dispatch('dispatchUserLoadedEvent');

								resolve(true);
							} else {
								resolve(false);
							}
						});
				}
			});
		},
		setRequestVerificationTokenAction({ commit }, token) {
			commit(SET_REQUEST_VERIFICATION_TOKEN, token);
		},
		setPrincipalTokenAction({ commit }, token) {
			commit(SET_ACCESS_TOKEN, token);
		},
		clearPrincipalAction({ commit }) {
			commit(SET_PRINCIPAL, {});
			commit(SET_ACCESS_TOKEN, null);
		}
	},
	getters: {
		user: state => state.principal,
		principalReady: state => {
			let result = false;

			if (state.principal && state.principal.EmployeeId) {
				result = true;
			}

			return result;
		},
		employeeId: state => {
			let id = 0;

			if (!objectIsEmpty(state.principal)) {
				id = parseInt(state.principal.EmployeeId);
			}

			return id;
		},
		employeePositions: state => {
			let result = [];
			if (state.principal && state.principal.Positions) {
				result = state.principal.Positions.map(p => p.Position);
			}
			return result;
		},
		employeeFullName: state => {
			let result = '';
			if (state.principal) {
				if (state.principal.FirstName)
					result = state.principal.FirstName;
				if (state.principal.LastName)
					result = result + ' ' + state.principal.LastName;
			}
			return result;
		},
		isAuthenticated: state => {
			let result = false;

			if (state.accessToken && !objectIsEmpty(state.principal)) {
				result = true;
			}

			return result;
		},
		accessToken: state => state.accessToken,
		requestVerificationToken: state => state.requestVerificationToken,
		useOidc: state => {
			let result = false;

			if (state.useOidc === 'true') {
				result = true;
			}

			return result;
		},
		doLogout: state => state.doLogout,
		enabledFeatureSwitches: state => {
			let result = [];

			if (state.principal && state.principal.EnabledFeatureSwitches) {
				result = state.principal.EnabledFeatureSwitches;

				if (result) {
					//convert ids to ints
					result = result.map(id => parseInt(id));
				}
			}

			return result;
		},
		hasFeatureSwitch: (state, getters) => featureSwitchIds => {
			let result = false;
			let checkFeatureSwitchIds = featureSwitchIds;

			if (!Array.isArray(checkFeatureSwitchIds)) {
				checkFeatureSwitchIds = [checkFeatureSwitchIds];
			}

			result = checkFeatureSwitchIds.some(id =>
				getters.enabledFeatureSwitches.includes(id)
			);

			return result;
		}
	}
};
